import React, { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Play, CheckCircle2, AlertTriangle, XCircle, Cpu, History } from "lucide-react";

const JOB_META = {
  itsync: { name: "Income Tax Sync", desc: "Incremental daily + full weekly from incometaxindia.gov.in", cadence: "Daily / Weekly full" },
  fcra:   { name: "FCRA Refresh", desc: "Public dashboard crawl; status + list from fcraonline.gov.in", cadence: "Monthly" },
  darpan: { name: "NGO Darpan Refresh", desc: "NPO Darpan crawler (to be built) — currently stalest source", cadence: "Monthly" },
  ngomap: { name: "Entity Mapping", desc: "Rebuilds the crosswalk; applies reviewer decisions first", cadence: "Weekly (after sources)" },
};

const STATUS = {
  success: { cls: "text-emerald-600", Icon: CheckCircle2 },
  stale: { cls: "text-amber-600", Icon: AlertTriangle },
  failed: { cls: "text-rose-600", Icon: XCircle },
};

export default function AdminJobs() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/v1/admin/jobs").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const trigger = async (job) => {
    setBusy(job);
    try {
      await api.post(`/v1/admin/jobs/${job}/run`);
      toast.success(`${JOB_META[job].name} triggered`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin · data pipeline"
        title="Sync Jobs"
        subtitle="Trigger and monitor the batch jobs that keep each registry and the crosswalk fresh."
      />

      <div className="grid gap-4 lg:grid-cols-2 mb-10">
        {data?.jobs.map((j) => {
          const meta = JOB_META[j.job] || {};
          const st = STATUS[j.status] || STATUS.success;
          return (
            <div key={j.job} data-testid={`job-card-${j.job}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 backdrop-blur-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-blue-400" />
                    <h3 className="font-semibold text-slate-100">{meta.name || j.job}</h3>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-400 max-w-sm">{meta.desc}</p>
                </div>
                <span data-testid={`job-status-${j.job}`} className={`inline-flex items-center gap-1 text-xs font-mono ${st.cls}`}>
                  <st.Icon className="w-3.5 h-3.5" /> {j.status}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3 font-mono text-[11px]">
                <div>
                  <div className="text-slate-600 uppercase tracking-wider">Freshness</div>
                  <div className="text-slate-200">{formatDate(j.freshness)}</div>
                </div>
                <div>
                  <div className="text-slate-600 uppercase tracking-wider">Records</div>
                  <div className="text-slate-200">{j.records?.toLocaleString?.() || "—"}</div>
                </div>
                <div>
                  <div className="text-slate-600 uppercase tracking-wider">Cadence</div>
                  <div className="text-slate-200">{meta.cadence}</div>
                </div>
              </div>

              <button
                data-testid={`job-trigger-btn-${j.job}`}
                disabled={busy === j.job}
                onClick={() => trigger(j.job)}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-blue-400 disabled:opacity-50 transition-colors duration-150"
              >
                <Play className="w-3.5 h-3.5" /> {busy === j.job ? "Running…" : "Run now"}
              </button>
            </div>
          );
        })}
      </div>

      <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-200 mb-3">
        <History className="w-4 h-4 text-slate-400" /> Recent mapping runs
      </h3>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80">
            <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Run</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Entities</th>
              <th className="px-4 py-3">Confirmed</th>
              <th className="px-4 py-3">Review</th>
              <th className="px-4 py-3">Finished</th>
            </tr>
          </thead>
          <tbody>
            {data?.mapping_runs.map((r) => (
              <tr key={r.run} className="border-t border-slate-800/60">
                <td className="px-4 py-3 font-mono text-slate-200">#{r.run}</td>
                <td className="px-4 py-3 text-emerald-600 font-mono text-xs">{r.status}</td>
                <td className="px-4 py-3 font-mono text-slate-300">{r.stats?.entities?.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-emerald-700">{r.stats?.links_by_tier?.confirmed?.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-amber-700">{r.stats?.links_by_tier?.review?.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{formatDate(r.finished_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
